import { formatDate, registerLocaleData, CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  LOCALE_ID,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import esCo from '@angular/common/locales/es-CO';
import { PageTitleService } from '../../shared/services';
import { Sale, Client, Product } from '../../shared/interfaces';
import { SalesService, ReceiptFormat } from '../../services/sales.service';
import { BaseAuthComponent } from '../../shared/common/BaseAuthComponent.class';
import { ToastService } from '../../shared/services';
import { ClientLookup } from '../../shared/components/client-lookup/client-lookup';
import { ProductLookup } from '../../shared/components/product-lookup/product-lookup';
import { ProductSalesModal } from '../agendas/product-sales-modal/product-sales-modal';
import { SaleDetailDrawer } from './sale-detail-drawer/sale-detail-drawer';
import flatpickr from 'flatpickr';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

registerLocaleData(esCo);

const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

type SalesPeriod = 'today' | 'week' | 'month' | 'all' | 'custom';

const FLATPICKR_LOCALE: any = {
  firstDayOfWeek: 1,
  weekdays: {
    shorthand: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    longhand: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
  },
  months: {
    shorthand: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    longhand: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  },
};

@Component({
  selector: 'app-sales',
  standalone: true,
  imports: [
    CommonModule,
    ClientLookup,
    ProductLookup,
    ProductSalesModal,
    SaleDetailDrawer,
  ],
  templateUrl: './sales.html',
  styleUrl: './sales.css',
  providers: [{ provide: LOCALE_ID, useValue: 'es-CO' }],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class Sales
  extends BaseAuthComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('fromDateInput', { static: false })
  fromDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('toDateInput', { static: false })
  toDateInput?: ElementRef<HTMLInputElement>;

  sales: Sale[] = [];
  loading = false;
  downloadingReceipts = signal<Set<string>>(new Set());
  page = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 1;

  // KPI data
  get totalSalesAmount(): number {
    return this.sales.reduce((sum, sale) => sum + (sale.amount || 0), 0);
  }

  get totalPaidAmount(): number {
    return this.sales.reduce((sum, sale) => sum + (sale.paidAmount || 0), 0);
  }

  get averageTicket(): number {
    if (this.sales.length === 0) return 0;
    return this.totalSalesAmount / this.sales.length;
  }

  get totalPendingAmount(): number {
    return Math.max(0, this.totalSalesAmount - this.totalPaidAmount);
  }

  get paidPercent(): number {
    if (this.totalSalesAmount <= 0) return 0;
    return Math.min(100, Math.round((this.totalPaidAmount / this.totalSalesAmount) * 100));
  }

  // Filters
  fromDate: Date | null = null;
  toDate: Date | null = null;
  selectedClient: Client | null = null;
  selectedProduct: Product | null = null;
  showFilters = false;
  period: SalesPeriod = 'today';

  readonly periodOptions: { value: Exclude<SalesPeriod, 'custom'>; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Últimos 7 días' },
    { value: 'month', label: 'Este mes' },
    { value: 'all', label: 'Todo' },
  ];

  // New Sale Modal
  showNewSaleModal = false;

  // Detail Drawer
  selectedSale: Sale | null = null;
  showDetailDrawer = false;

  private fromDatePicker?: FlatpickrInstance;
  private toDatePicker?: FlatpickrInstance;

  titleService = inject(PageTitleService);
  salesService = inject(SalesService);
  toastService = inject(ToastService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Ventas',
      subtitle: 'Lo que has vendido, con sus pagos y productos',
    });
    this.applyPeriodDates('today');
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initDatePickers();
    });
  }

  ngOnDestroy(): void {
    this.fromDatePicker?.destroy();
    this.toDatePicker?.destroy();
  }

  private initDatePickers(): void {
    if (this.fromDateInput?.nativeElement) {
      this.fromDatePicker = flatpickr(this.fromDateInput.nativeElement, {
        dateFormat: 'Y-m-d',
        allowInput: true,
        defaultDate: this.fromDate || undefined,
        locale: FLATPICKR_LOCALE,
        onChange: (selectedDates) => {
          this.fromDate = selectedDates.length > 0 ? selectedDates[0] : null;
          this.period = 'custom';
          if (this.toDatePicker && this.fromDate) {
            this.toDatePicker.set('minDate', this.fromDate);
          }
          this.onFilterChange();
        },
      });
    }

    if (this.toDateInput?.nativeElement) {
      this.toDatePicker = flatpickr(this.toDateInput.nativeElement, {
        dateFormat: 'Y-m-d',
        allowInput: true,
        defaultDate: this.toDate || undefined,
        minDate: this.fromDate || undefined,
        locale: FLATPICKR_LOCALE,
        onChange: (selectedDates) => {
          // Include the whole end day
          this.toDate = selectedDates.length > 0
            ? new Date(selectedDates[0].getFullYear(), selectedDates[0].getMonth(), selectedDates[0].getDate(), 23, 59, 59, 999)
            : null;
          this.period = 'custom';
          this.onFilterChange();
        },
      });
    }
  }

  // Status helpers
  readonly statusConfig: Record<string, { label: string; class: string }> = {
    paid: {
      label: 'Pagado',
      class: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
    },
    pending: {
      label: 'Pendiente',
      class: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
    },
    partially_paid: {
      label: 'Parcial',
      class: 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
    },
    failed: {
      label: 'Fallido',
      class: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20',
    },
    processing: {
      label: 'Procesando',
      class: 'bg-gray-50 text-gray-600 ring-gray-500/20 dark:bg-gray-500/10 dark:text-gray-400 dark:ring-gray-500/20',
    },
    refunded: {
      label: 'Reembolsado',
      class: 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-500/10 dark:text-purple-400 dark:ring-purple-500/20',
    },
  };

  // Method helpers
  readonly methodConfig: Record<string, string> = {
    epayco_link: 'Link de pago',
    manual: 'Manual',
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    credit: 'Saldo a favor',
    client_credit: 'Saldo a favor',
    other: 'Otro',
  };

  getStatusLabel(status: string): string {
    return this.statusConfig[status]?.label || status || '-';
  }

  getStatusClass(status: string): string {
    return this.statusConfig[status]?.class || this.statusConfig['processing'].class;
  }

  getMethodLabel(method: string): string {
    return this.methodConfig[method] || method || '-';
  }

  // Pagination
  onPageChange = (p: number) => {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.load();
  };

  // Filters
  onFilterChange(): void {
    this.page = 1;
    this.load();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  clearFilters(): void {
    this.selectedClient = null;
    this.selectedProduct = null;
    this.setPeriod('all');
  }

  setPeriod(period: Exclude<SalesPeriod, 'custom'>): void {
    this.applyPeriodDates(period);
    this.syncPickers();
    this.onFilterChange();
  }

  private applyPeriodDates(period: Exclude<SalesPeriod, 'custom'>): void {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    this.period = period;
    switch (period) {
      case 'today':
        this.fromDate = startOfDay(now);
        this.toDate = endOfToday;
        break;
      case 'week':
        this.fromDate = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
        this.toDate = endOfToday;
        break;
      case 'month':
        this.fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        this.toDate = endOfToday;
        break;
      default:
        this.fromDate = null;
        this.toDate = null;
    }
  }

  private syncPickers(): void {
    if (this.fromDate) this.fromDatePicker?.setDate(this.fromDate, false);
    else this.fromDatePicker?.clear(false);
    if (this.toDate) this.toDatePicker?.setDate(this.toDate, false);
    else this.toDatePicker?.clear(false);
    this.toDatePicker?.set('minDate', this.fromDate || undefined);
  }

  get hasActiveFilters(): boolean {
    return !!(this.fromDate || this.toDate || this.selectedClient || this.selectedProduct);
  }

  /** Client/product/custom-date filters hidden behind the "Filtrar" button */
  get extraFilterCount(): number {
    return (this.selectedClient ? 1 : 0) + (this.selectedProduct ? 1 : 0) + (this.period === 'custom' ? 1 : 0);
  }

  /** Describes the active period for the summary, e.g. "hoy" or "este mes" */
  get periodPhrase(): string {
    const fmt = (d: Date) => formatDate(d, 'd MMM', 'es-CO');
    switch (this.period) {
      case 'today':
        return 'hoy';
      case 'week':
        return 'en los últimos 7 días';
      case 'month':
        return 'este mes';
      case 'all':
        return 'en total';
      default:
        if (this.fromDate && this.toDate) return `del ${fmt(this.fromDate)} al ${fmt(this.toDate)}`;
        if (this.fromDate) return `desde el ${fmt(this.fromDate)}`;
        if (this.toDate) return `hasta el ${fmt(this.toDate)}`;
        return 'en total';
    }
  }

  onClientSelected(client: Client): void {
    this.selectedClient = client;
    this.onFilterChange();
  }

  onClientCleared(): void {
    this.selectedClient = null;
    this.onFilterChange();
  }

  onProductSelected(product: Product): void {
    this.selectedProduct = product;
    this.onFilterChange();
  }

  onProductCleared(): void {
    this.selectedProduct = null;
    this.onFilterChange();
  }

  // Data loading
  load(): void {
    this.loading = true;

    this.salesService
      .getSales({
        page: this.page,
        pageSize: this.pageSize,
        fromDate: this.fromDate || undefined,
        toDate: this.toDate || undefined,
        clientId: this.selectedClient?.userId || undefined,
        productId: this.selectedProduct?.productId || undefined,
      })
      .subscribe({
        next: (res) => {
          this.sales = res.sales;
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalCount = res.totalCount;
          this.totalPages = res.totalPages;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastService.show('No se pudieron cargar las ventas. Recarga la página.', 'error');
        },
      });
  }

  // New Sale Modal
  openNewSale(): void {
    this.showNewSaleModal = true;
  }

  onNewSaleClose(): void {
    this.showNewSaleModal = false;
  }

  onSaleCompleted(): void {
    this.load();
  }

  // Detail Drawer
  openDetail(sale: Sale): void {
    this.selectedSale = sale;
    this.showDetailDrawer = true;
  }

  closeDetail(): void {
    this.showDetailDrawer = false;
    this.selectedSale = null;
  }

  // Receipt download
  downloadReceipt(paymentId: string, format: ReceiptFormat = 'half-letter'): void {
    if (!paymentId) {
      this.toastService.show('Esta venta no tiene comprobante disponible.', 'error');
      return;
    }

    if (this.downloadingReceipts().has(paymentId)) {
      return;
    }

    this.downloadingReceipts.update((set: Set<string>) => new Set(set).add(paymentId));
    this.salesService.downloadReceipt(paymentId, format).subscribe({
      next: (blob) => {
        if (blob.size === 0) {
          blob.text().then(text => {
            try {
              const error = JSON.parse(text);
              this.toastService.show(error.message || 'Error al generar el comprobante', 'error');
            } catch {
              this.toastService.show('Error al generar el comprobante PDF', 'error');
            }
          }).catch(() => {
            this.toastService.show('Error al generar el comprobante PDF', 'error');
          });
          this.removeDownloading(paymentId);
          return;
        }

        const pdfBlob = blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprobante-${paymentId.slice(0, 8)}${format === 'ticket' ? '-tirilla' : ''}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.removeDownloading(paymentId);
        this.toastService.show('Comprobante descargado', 'success');
      },
      error: (err) => {
        this.removeDownloading(paymentId);
        if (err.error instanceof Blob) {
          err.error.text().then((text: string) => {
            try {
              const error = JSON.parse(text);
              this.toastService.show(error.message || 'Error al descargar el comprobante', 'error');
            } catch {
              this.toastService.show('Error al descargar el comprobante', 'error');
            }
          }).catch(() => {
            this.toastService.show('Error al descargar el comprobante', 'error');
          });
        } else {
          this.toastService.show(err.error?.message || 'Error al descargar el comprobante', 'error');
        }
      },
    });
  }

  private removeDownloading(paymentId: string): void {
    this.downloadingReceipts.update((set: Set<string>) => {
      const newSet = new Set(set);
      newSet.delete(paymentId);
      return newSet;
    });
  }

  isDownloadingReceipt(paymentId: string): boolean {
    return this.downloadingReceipts().has(paymentId);
  }

  getItemsSummary(items: any[]): string {
    if (!items || !Array.isArray(items)) return '-';
    return items.map((item: any) => `${item.quantity || 1}x ${item.itemName || item.itemType}`).join(', ') || '-';
  }

  formatCurrency(value: number): string {
    return currencyFormatter.format(value ?? 0);
  }

  // Skeleton helpers
  skeletonRows = Array(8).fill(0);
}
